import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { admin, api } from '../../api/client.js'
import { useApi } from '../../api/useApi.js'
import { btn, Field, inputClass, Notice, PhotoUpload, useAction } from '../../components/admin/ui.jsx'
import { pickI18n } from '../../i18n'
import { ErrorState, Loading } from '../states.jsx'
import { AdminNav } from './AdminFeedersPage.jsx'

const blank = { name: '', description: { en: '', ru: '', el: '' }, photo_url: null, home_feeder_id: '' }

function CatEditor({ initial, feeders, onDone, onCancel }) {
  const { t } = useTranslation()
  const [cat, setCat] = useState(() => ({ ...blank, ...initial, description: { ...blank.description, ...initial?.description } }))
  const { status, busy, run } = useAction()
  const submit = async (e) => {
    e.preventDefault()
    const body = {
      name: cat.name.trim(),
      description: Object.fromEntries(Object.entries(cat.description).filter(([, v]) => v)),
      photo_url: cat.photo_url || null,
      home_feeder_id: cat.home_feeder_id || null,
    }
    const res = await run(() => (initial?.id ? admin.updateCat(initial.id, body) : admin.createCat(body)))
    if (res) onDone()
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4 rounded-3xl border-2 border-teal p-5">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label={t('admin.catsPage.name')}>
          <input className={inputClass} required maxLength={80} value={cat.name} onChange={(e) => setCat({ ...cat, name: e.target.value })} />
        </Field>
        <Field label={t('admin.catsPage.home')}>
          <select className={inputClass} value={cat.home_feeder_id ?? ''} onChange={(e) => setCat({ ...cat, home_feeder_id: e.target.value })}>
            <option value="">{t('admin.catsPage.none')}</option>
            {feeders.map((f) => (
              <option key={f.id} value={f.id}>
                {pickI18n(f.name, 'en')}
              </option>
            ))}
          </select>
        </Field>
        {['en', 'ru', 'el'].map((l) => (
          <Field key={l} label={t(`admin.catsPage.desc${l[0].toUpperCase()}${l[1]}`)} className="md:col-span-2">
            <input
              className={inputClass}
              value={cat.description[l]}
              onChange={(e) => setCat({ ...cat, description: { ...cat.description, [l]: e.target.value } })}
            />
          </Field>
        ))}
        <div className="flex flex-col gap-1.5 text-sm font-extrabold md:col-span-2">
          {t('admin.form.photo')}
          <PhotoUpload value={cat.photo_url} onChange={(url) => setCat({ ...cat, photo_url: url })} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={busy} className={btn.primary}>
          {t('admin.save')}
        </button>
        <button type="button" className={btn.ghost} onClick={onCancel}>
          {t('admin.cancel')}
        </button>
        <Notice status={status} />
      </div>
    </form>
  )
}

/** "This cat looks unwell" reports waiting for a volunteer. */
function CatReports() {
  const { t } = useTranslation()
  const reports = useApi(() => admin.catReports())
  if (!reports.data?.length) return null
  const resolve = async (r) => {
    const note = window.prompt(t('admin.reports.resolvePrompt'), '')
    if (note === null) return
    await admin.resolveReport(r.id, note)
    reports.reload()
  }
  return (
    <section className="flex flex-col gap-3 rounded-3xl border-[1.5px] border-live/50 p-5">
      <h2 className="text-xl font-black">🩺 {t('admin.reports.title', { count: reports.data.length })}</h2>
      {reports.data.map((r) => (
        <div key={r.id} className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-3 last:border-0">
          <div className="flex flex-col">
            <Link to={`/cats/${r.cat_id}`} className="font-extrabold text-ink">
              {r.cat_name} · {t(`catPage.kinds.${r.kind}`)}
            </Link>
            <span className="text-muted">{r.text}</span>
            <span className="text-xs text-subtle">
              {r.from ?? '—'} · {new Date(r.created_at).toLocaleString()}
            </span>
          </div>
          <button type="button" className={btn.primary} onClick={() => resolve(r)}>
            {t('admin.reports.resolve')}
          </button>
        </div>
      ))}
    </section>
  )
}

export default function AdminCatsPage() {
  const { t, i18n } = useTranslation()
  const cats = useApi(() => api.cats())
  const feeders = useApi(() => admin.feeders())
  const [editing, setEditing] = useState(null) // null | 'new' | cat
  const { status, run } = useAction()

  if (cats.loading || feeders.loading) return <Loading />
  if (cats.error || feeders.error) return <ErrorState />
  const feederName = (id) => pickI18n(feeders.data.find((f) => f.id === id)?.name, i18n.resolvedLanguage) || '—'
  const done = () => {
    setEditing(null)
    cats.reload()
  }

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-4 pt-8 pb-16 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-3">
          <h1 className="text-[clamp(28px,3.5vw,40px)] font-black tracking-[-1px]">{t('admin.cats')}</h1>
          <AdminNav />
        </div>
        <button type="button" className={btn.primary} onClick={() => setEditing('new')}>
          + {t('admin.catsPage.newCat')}
        </button>
      </div>
      {editing && (
        <CatEditor
          key={editing === 'new' ? 'new' : editing.id}
          initial={editing === 'new' ? null : editing}
          feeders={feeders.data}
          onDone={done}
          onCancel={() => setEditing(null)}
        />
      )}
      <Notice status={status} />
      <CatReports />
      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
        {cats.data.map((c) => (
          <article key={c.id} className="flex flex-col overflow-hidden rounded-2xl bg-sand">
            <img src={c.photo_url ?? '/brand/urbanpurr-app-icon.svg'} alt="" className="aspect-square w-full object-cover" />
            <div className="flex flex-1 flex-col gap-1 p-4">
              <span className="text-lg font-extrabold">{c.name}</span>
              <span className="text-xs font-bold text-subtle">{feederName(c.home_feeder_id)}</span>
              <span className="text-sm text-muted">{pickI18n(c.description, i18n.resolvedLanguage)}</span>
              <div className="mt-auto flex gap-2 pt-2">
                <button type="button" className={btn.ghost} onClick={() => setEditing(c)}>
                  {t('admin.edit')}
                </button>
                <button
                  type="button"
                  className={btn.danger}
                  onClick={() => window.confirm(`${t('admin.delete')} ${c.name}?`) && run(() => admin.deleteCat(c.id)).then(cats.reload)}
                >
                  {t('admin.delete')}
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </main>
  )
}
